import {
  useEffect, useMemo, useRef, useState
} from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AlertTriangle, ArrowLeft, Loader2, Save
} from 'lucide-react';
import ROUTE from 'src/utils/routeConstants';
import { isFleetOnlyModeEnabled } from 'src/utils/appMode';
import { isTyreManagementV2Enabled } from 'src/utils/tyreManagementFeature';
import { useTheme as useAppTheme } from 'src/context/ThemeContext';
import PageBreadcrumb, { hasNestedLinks } from 'src/features/tyre-management/components/PageBreadcrumb';
import { Button } from 'src/features/tyre-management/components/ui/button';
import { vehicleDetailsApi } from 'src/features/vehicle-details/api/vehicleDetailsApi';
import VehicleFormSections from './forms/VehicleFormSections';
import {
  emptyVehicleForm,
  normalizeVehiclePayload,
  parseFieldErrors,
  validateVehicleForm,
} from './forms/vehicleFormState';
import { vehicleListApi } from './api/vehicleListApi';
import 'src/features/tyre-management/tyre-management.css';
import 'src/features/vehicle-details/vehicle-details.css';

const sectionLinks = [
  ['edit-identity', 'Identity'],
  ['edit-assignment', 'Assignment'],
  ['edit-operation', 'Fuel & operation'],
  ['edit-telematics', 'Tracking (optional)'],
  ['edit-modules', 'Tyre tracking'],
];

const crumbs = [
  { label: 'Vehicle Management' },
  { label: 'Vehicles', href: ROUTE.vehicles },
  { label: 'New vehicle' },
];

const asList = (payload) => payload?.results || payload || [];

const VehicleCreatePage = () => {
  const history = useHistory();
  const location = useLocation();
  const { mode } = useAppTheme();
  const appSettings = useSelector((state) => state.settings.appSettings);
  const assetCodeMappingEnabled = Boolean(appSettings?.asset_code_mapping_enabled);
  const globalTyreEnabled = isTyreManagementV2Enabled(appSettings);
  const tyreDestinationAvailable = globalTyreEnabled && !isFleetOnlyModeEnabled(appSettings);
  const tyreHandoffRequested = new URLSearchParams(location.search).get('from') === 'tyres';
  const continueToTyreSetup = tyreHandoffRequested && tyreDestinationAvailable;
  const [form, setForm] = useState(() => ({
    ...emptyVehicleForm(),
    tyre_management_enabled: continueToTyreSetup,
  }));
  const [options, setOptions] = useState({
    sites: [], groups: [], departments: [], contractors: [], tanks: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const unblockRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      vehicleDetailsApi.getSites(),
      vehicleDetailsApi.getGroups(),
      vehicleDetailsApi.getDepartments(),
      vehicleDetailsApi.getContractors(),
      vehicleDetailsApi.getTanks(),
    ]).then(([sites, groups, departments, contractors, tanks]) => {
      if (!mounted) return;
      setOptions({
        sites: asList(sites),
        groups: asList(groups),
        departments: asList(departments),
        contractors: asList(contractors),
        tanks: asList(tanks),
      });
    }).catch((requestError) => setError(requestError.message)).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const unblock = history.block('You have unsaved vehicle changes. Leave without saving?');
    unblockRef.current = unblock;
    const beforeUnload = (event) => {
      event.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      unblock();
      if (unblockRef.current === unblock) unblockRef.current = null;
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [dirty, history]);

  const setField = (key, transform = (next) => next) => (event) => {
    const nextValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: transform(nextValue) }));
    setDirty(true);
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setError('');
  };
  const setCheckbox = (key) => (checked) => {
    setForm((current) => ({ ...current, [key]: checked }));
    setDirty(true);
    setError('');
  };

  const payload = useMemo(
    () => normalizeVehiclePayload(form, { includeTyreManagement: tyreDestinationAvailable }),
    [form, tyreDestinationAvailable]
  );

  const save = async (event) => {
    event.preventDefault();
    const clientErrors = await validateVehicleForm(form, { assetCodeMappingEnabled });
    if (Object.keys(clientErrors).length) {
      setFieldErrors(clientErrors);
      setError('Complete the required vehicle fields before saving.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await vehicleListApi.createVehicle(payload);
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      setDirty(false);
      history.push(`${ROUTE.vehicles}/${created.id}`, {
        vehicleCreated: true,
        vehicleId: created.id,
        creationSource: continueToTyreSetup ? 'tyre-workspace' : 'vehicle-list',
      });
    } catch (requestError) {
      setFieldErrors(parseFieldErrors(requestError.fields));
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="tyre-management-v2 vehicle-details-v2 vd-loading-page" data-theme={mode}>
        <div className="vd-edit-crumb"><PageBreadcrumb items={crumbs} /></div>
        <div className="vd-skeleton vd-skeleton--header" />
        <div className="vd-skeleton vd-skeleton--wide mt-6" />
      </main>
    );
  }

  return (
    <main className="tyre-management-v2 vehicle-details-v2 vd-edit-page min-h-[calc(100vh-64px)] px-4 py-5 sm:px-6 lg:px-8" data-theme={mode}>
      <Helmet>
        <title>New vehicle | Retrack FMS</title>
      </Helmet>
      <div className="vd-edit-crumb">
        <PageBreadcrumb items={crumbs} />
      </div>
      <header className="vd-edit-header">
        {!hasNestedLinks(crumbs) && (
          <Button variant="ghost" size="icon" aria-label="Back to vehicles" onClick={() => history.push(ROUTE.vehicles)}>
            <ArrowLeft />
          </Button>
        )}
        <div className="vd-edit-header__identity">
          <p className="vd-eyebrow">New vehicle</p>
          <h1 className="vd-registration">Add to the fleet register</h1>
        </div>
        <div className="vd-edit-header__actions">
          <Button variant="outline" onClick={() => history.push(ROUTE.vehicles)}>Cancel</Button>
          <Button type="submit" form="vehicle-create-form" disabled={saving}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            {saving ? 'Saving…' : 'Create vehicle'}
          </Button>
        </div>
      </header>
      {continueToTyreSetup && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800" role="status">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Tyre tracking is on for this vehicle. After saving, use the guided next step to choose its wheel layout and fit tyres.</span>
        </div>
      )}
      <div className="vd-edit-layout">
        <nav className="vd-section-index" aria-label="Create sections">
          {sectionLinks.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
        </nav>
        <form id="vehicle-create-form" className="vd-edit-form" onSubmit={save}>
          {error && (
            <div className="vd-form-error" role="alert">
              <AlertTriangle />
              {error}
            </div>
          )}
          <VehicleFormSections
            form={form}
            setField={setField}
            setCheckbox={setCheckbox}
            options={options}
            errors={fieldErrors}
            assetCodeMappingEnabled={assetCodeMappingEnabled}
            isCreate
            globalTyreEnabled={tyreDestinationAvailable}
          />
        </form>
      </div>
    </main>
  );
};

export default VehicleCreatePage;
